import { useCallback, useEffect, useMemo, useState } from 'react';
import { homeActions, uploadSpecimen } from '../../store';
import { useAppDispatch, useAppSelector } from '../redux';

export interface SpecimenUploadDialogProps {
  open?: boolean;
  onClose: () => void;
  onSuccessConfirm: () => void;
}

export interface SpecimenUploadLogicOutput {
  base64Specimen: string;
  disclaimerAccepted: boolean;
  isSpecimenFromState: boolean;
  canConfirm: boolean;
  showConfirm: boolean;
  showImagePreview: boolean;
  showRetryAndConfirm: boolean;
  showUploadAndTakePhoto: boolean;
  cameraOpen: boolean;
  onDisclaimerCheck: () => void;
  uploadSpecimen: (base64Specimen: string) => void;
  deleteSpecimen: () => void;
  confirmSpecimen: () => void;
  onPhotoTaken: (dataUrl: string) => void;
  setCameraOpen: (open: boolean) => void;
  onConfirm: () => void;
  handleClose: () => void;
}

export const useSpecimenUploadLogic = (
  props: SpecimenUploadDialogProps
): SpecimenUploadLogicOutput => {
  const { onClose, onSuccessConfirm } = props;
  const dispatch = useAppDispatch();

  /*
   * --- Selectors ---
   */

  const specimenFromState: string = useAppSelector(
    (state) =>
      state.features.home.toDoActivities?.value?.specimenState?.data
        ?.imageBase64
  );

  const uploadSpecimenRequestStatus = useAppSelector(
    (state) => state.features.home.uploadSpecimenRequestStatus
  );

  /*
   * --- Component state variables ---
   */
  const [base64Specimen, setBase64Specimen] = useState<string | undefined>();
  const [specimenConfirmed, setSpecimenConfirmed] = useState<boolean>(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState<boolean>(false);
  const [isSpecimenFromState, setIsSpecimenFromState] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);

  /*
   * --- Memos ---
   */

  // show image preview only when base64 is present
  const showImagePreview = useMemo(
    () => base64Specimen?.length > 0,
    [base64Specimen]
  );

  // user can confirm only if has uploaded the specimen,
  // confirmed and accepted the disclaimer
  const canConfirm = useMemo(
    () => showImagePreview && specimenConfirmed && disclaimerAccepted,
    [showImagePreview, specimenConfirmed, disclaimerAccepted]
  );

  // show confirm button and disclaimer only when uploaded specimen and confirmed
  const showConfirm = useMemo(
    () => showImagePreview,
    [showImagePreview, specimenConfirmed]
  );

  // show retry and save buttons only when uploaded specimen but not confirmed
  const showRetryAndConfirm = useMemo(
    () => showImagePreview && !specimenConfirmed,
    [showImagePreview, specimenConfirmed]
  );

  // show upload and take photo buttons only when specimen is not uploaded and the camera is inactive
  const showUploadAndTakePhoto = useMemo(
    () => !showImagePreview && !cameraOpen,
    [showImagePreview, cameraOpen]
  );

  /*
   * --- Effects ---
   */

  // set specimen from state when changed
  useEffect(() => setSpecimenFromState(specimenFromState), [specimenFromState]);

  // when upload is succeeded, close dialog and call onSuccessConfirm
  useEffect(() => {
    if (uploadSpecimenRequestStatus === 'succeeded') {
      handleClose();
      onSuccessConfirm?.();
    }
  }, [uploadSpecimenRequestStatus]);

  /*
   * --- Functions/callbacks ---
   */

  // set specimen from state and set confirmed and disclaimer accepted
  const setSpecimenFromState = (specimenFromState) => {
    if (specimenFromState?.length > 0) {
      setBase64Specimen(specimenFromState);
      setSpecimenConfirmed(true);
      setDisclaimerAccepted(true);
      setIsSpecimenFromState(true)
      setCameraOpen(false);
    } else {
      deleteSpecimen();
    }
  };

  // when uploading a new specimen, reset all variables
  const uploadBase64Specimen = (base64Specimen: string) => {
    setBase64Specimen(base64Specimen);
    setSpecimenConfirmed(true);
    setDisclaimerAccepted(false);
  };

  // confirm specimen only if a file has been loaded
  const confirmSpecimen = useCallback(
    () => setSpecimenConfirmed(base64Specimen?.length > 0),
    [base64Specimen]
  );

  // reset specimen and confirmation
  const deleteSpecimen = () => {
    setBase64Specimen(undefined);
    setSpecimenConfirmed(false);
    setDisclaimerAccepted(false);
    setIsSpecimenFromState(false);
    setCameraOpen(false);
  };

  // handle image taken from camera or webcam
  const onPhotoTaken = (dataUrl: string) => {
    if (dataUrl?.length) {
      setBase64Specimen(dataUrl);
      setCameraOpen(false);
    }
  };

  // toggle disclaimer check
  const onDisclaimerCheck = useCallback(
    () => setDisclaimerAccepted(!disclaimerAccepted),
    [disclaimerAccepted]
  );

  // reset state when closing
  const handleClose = useCallback(() => {
    dispatch(homeActions.clearUploadSpecimenRequestStatus());
    setSpecimenFromState(specimenFromState);
    onClose?.();
  }, [onClose, specimenFromState]);

  // call props onPressButton when confirm
  // the function passed as prop will handle all the logic
  const onConfirm = useCallback(() => {
    if (canConfirm) {
      dispatch(
        uploadSpecimen({
          imageBase64: base64Specimen,
          disclaimerAccepted,
        })
      );
    }
  }, [onSuccessConfirm, handleClose, canConfirm]);

  return {
    base64Specimen,
    disclaimerAccepted,
    isSpecimenFromState,
    canConfirm,
    showConfirm,
    showImagePreview,
    showRetryAndConfirm,
    showUploadAndTakePhoto,
    cameraOpen,
    uploadSpecimen: uploadBase64Specimen,
    onDisclaimerCheck,
    onConfirm,
    handleClose,
    deleteSpecimen,
    confirmSpecimen,
    onPhotoTaken,
    setCameraOpen,
  };
};
